import clsx from "clsx";
import { Radio, RadioGroup, Label } from "@headlessui/react";
import { CheckCircleIcon } from "@phosphor-icons/react";

interface RadioOption {
	id: string | number;
	value: string;
	label?: string;
	icon?: React.ReactNode;
}

interface RadioGroupFieldProps {
	id: string;
	label: string;
	value: string;
	options: RadioOption[];
	onChange: (value: string) => void;
	required?: boolean;
	error?: string;
	disabled?: boolean;
	className?: string;
	gridCols?: number;
	showIcons?: boolean;
	disabledOptions?: string[];
	navRef?: boolean;
}

export function RadioGroupField({
	id,
	label,
	value,
	options = [],
	onChange,
	error,
	required = false,
	disabled = false,
	className = "",
	gridCols = 2,
	showIcons = false,
	disabledOptions = [],
	navRef = false,
}: RadioGroupFieldProps) {
	const colClass =
		{
			1: "bizF-grid-cols-1",
			2: "bizF-grid-cols-2",
			3: "bizF-grid-cols-3",
			4: "bizF-grid-cols-4",
			5: "bizF-grid-cols-5",
			6: "bizF-grid-cols-6",
		}[gridCols] || "bizF-grid-cols-2";

	return (
		<div className={className}>
			<RadioGroup
				id={id}
				aria-label={label}
				value={value}
				onChange={onChange}
				disabled={disabled}
				aria-required={required}
				aria-describedby={error ? `${id}-error` : ""}
				aria-invalid={!!error}
			>
				<Label className="bizF-text-base bizF-font-semibold bizF-leading-6 bizF-text-gray-900">
					{label}
				</Label>
				<div
					className={clsx(
						navRef ? "bizF-grid-cols-2" : colClass,
						`bizF-grid bizF-mt-1 bizF-gap-x-2 bizF-gap-y-2 md:bizF-gap-x-2`
					)}
				>
					{options.map((item) => {
						const isOptionDisabled = disabledOptions.includes(item.value);
						const optionId = `${id}-option-${item.id}`;
						return (
							<Radio
								key={item.value}
								value={item.value}
								disabled={isOptionDisabled}
								id={optionId}
								aria-describedby={
									isOptionDisabled ? `${optionId}-disabled` : undefined
								}
								className={({ checked }) =>
									clsx(
										disabled || isOptionDisabled
											? "bizF-opacity-50 bizF-cursor-not-allowed"
											: "bizF-cursor-pointer",
										isOptionDisabled ? "bizF-bg-gray-50" : "bizF-bg-white",
										checked
											? "bizF-border-[--bizF-theme-color] bizF-ring-1 bizF-ring-[--bizF-theme-color]"
											: "bizF-border-gray-300",
										error
											? "bizF-border-red-600 bizF-ring-0 bizF-ring-red-600"
											: "bizF-border-gray-300",
										"bizF-relative bizF-flex bizF-flex-1 bizF-rounded-lg bizF-border bizF-p-2 bizF-shadow-sm focus:bizF-outline-none focus-visible:bizF-outline-[--bizF-theme-color] md:bizF-p-3"
									)
								}
							>
								{({ checked }) => (
									<>
										<span className="bizF-flex bizF-flex-1">
											<span
												className={clsx(
													navRef ? "" : "",
													"bizF-flex bizF-flex-col bizF-items-start bizF-justify-center"
												)}
											>
												{showIcons && item.icon && (
													<div
														className={clsx(
															!checked
																? " bizF-text-gray-900"
																: "bizF-text-[--bizF-theme-color]"
														)}
													>
														{item.icon}
													</div>
												)}
												<Label
													as="span"
													className={clsx(
														!checked
															? "bizF-flex bizF-text-sm bizF-font-medium bizF-text-gray-900"
															: "",
														"bizF-text-wrap bizF-text-[--bizF-theme-color] bizF-flex bizF-text-sm bizF-font-medium bizF-leading-4"
													)}
												>
													{item.label || item.value}
												</Label>
											</span>
										</span>
										<CheckCircleIcon
											className={clsx(
												!checked ? "bizF-invisible" : "",
												"bizF-h-5 bizF-w-5 bizF-text-[--bizF-theme-color]"
											)}
											aria-hidden="true"
											weight="fill"
										/>
										<span
											className={clsx(
												checked
													? "bizF-border-[--bizF-theme-color]"
													: "bizF-border-transparent",
												"bizF-pointer-events-none bizF-absolute bizF-rounded-lg"
											)}
											aria-hidden="true"
										/>
									</>
								)}
							</Radio>
						);
					})}
				</div>
			</RadioGroup>
			{error && (
				<p
					id={`${id}-error`}
					className="bizF-mt-1 bizF-text-xs bizF-text-red-600"
					role="alert"
					aria-live="polite"
				>
					{error}
				</p>
			)}
		</div>
	);
}
